import React, { useState, useEffect } from 'react';
import axios from 'axios';
import EmailComposer from '../components/EmailComposer';

const defaultBlock = { type: 'richtext', value: '', align: 'left' };

const EmailTemplateGenerator = () => {
  const [templateFields, setTemplateFields] = useState({
    title: '',
    description: '',
    subject: '',
    previewText: '',
    to: [],
    cc: [],
    bcc: [],
    toProgrammed: true,
    ccProgrammed: true,
    bccProgrammed: true
  });
  const [bodyBlocks, setBodyBlocks] = useState([{ ...defaultBlock }]);
  const [saveResult, setSaveResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testTemplate, setTestTemplate] = useState(null);
  const [testAttributes, setTestAttributes] = useState({});
  const [testEmailFields, setTestEmailFields] = useState({
    to: [],
    cc: [],
    bcc: []
  });
  const [testBodyBlocks, setTestBodyBlocks] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // Load templates
  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/emailtemplates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Load templates when modal opens
  useEffect(() => {
    if (showTemplatesModal) {
      loadTemplates();
    }
  }, [showTemplatesModal]);

  // Function to extract attributes from text content
  const extractAttributes = (text) => {
    if (!text) return [];
    const matches = text.match(/\$\{([^}]+)\}/g);
    if (!matches) return [];
    return matches.map(match => match.slice(2, -1)); // Remove ${ and }
  };

  // Function to extract attributes from body blocks
  const extractAttributesFromBlocks = (blocks) => {
    const attributes = [];
    blocks.forEach(block => {
      if (block.type === 'richtext' && block.value) {
        attributes.push(...extractAttributes(block.value));
      } else if (block.type === 'button' && block.value) {
        if (block.value.text) attributes.push(...extractAttributes(block.value.text));
        if (block.value.link) attributes.push(...extractAttributes(block.value.link));
      } else if (block.type === 'image' && block.value) {
        if (block.value.src) attributes.push(...extractAttributes(block.value.src));
      }
    });
    return attributes;
  };

  // Load template for editing/duplicating
  const loadTemplate = async (templateId, forDuplication = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/emailtemplates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const template = response.data;
      
      if (forDuplication) {
        // For duplication, clear the ID and modify title
        setTemplateFields({
          title: `${template.title} (Copy)`,
          description: template.description,
          subject: template.subject,
          previewText: template.preview_text,
          to: template.to || [],
          cc: template.cc || [],
          bcc: template.bcc || [],
          toProgrammed: template.to_programmed,
          ccProgrammed: template.cc_programmed,
          bccProgrammed: template.bcc_programmed
        });
        setBodyBlocks(template.body_blocks || [{ ...defaultBlock }]);
        setIsEditing(false);
        setEditingTemplate(null);
      } else {
        // For editing, include the ID
        setTemplateFields({
          title: template.title,
          description: template.description,
          subject: template.subject,
          previewText: template.preview_text,
          to: template.to || [],
          cc: template.cc || [],
          bcc: template.bcc || [],
          toProgrammed: template.to_programmed,
          ccProgrammed: template.cc_programmed,
          bccProgrammed: template.bcc_programmed
        });
        setBodyBlocks(template.body_blocks || [{ ...defaultBlock }]);
        setIsEditing(true);
        setEditingTemplate(template);
      }
      
      setShowTemplatesModal(false);
      setSaveResult(null);
    } catch (error) {
      console.error('Failed to load template:', error);
    }
  };

  // Load template for testing
  const loadTemplateForTest = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/emailtemplates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const template = response.data;
      setTestTemplate(template);
      
      // Initialize test email fields
      setTestEmailFields({
        to: template.to || [],
        cc: template.cc || [],
        bcc: template.bcc || []
      });
      
      // Initialize test body blocks
      setTestBodyBlocks(template.body_blocks || [{ ...defaultBlock }]);
      
      // Initialize test attributes
      const attributes = template.allowed_attributes || [];
      const initialAttributes = {};
      attributes.forEach(attr => {
        initialAttributes[attr] = '';
      });
      setTestAttributes(initialAttributes);
      
      setShowTemplatesModal(false);
      setShowTestModal(true);
      setTestResult(null);
    } catch (error) {
      console.error('Failed to load template for testing:', error);
    }
  };

  // Function to replace attributes in text
  const replaceAttributes = (text, attributes) => {
    if (!text) return text;
    let result = text;
    Object.keys(attributes).forEach(key => {
      const regex = new RegExp(`\\$\\{${key}\\}`, 'g');
      result = result.replace(regex, attributes[key] || `[${key}]`);
    });
    return result;
  };

  // Function to replace attributes in body blocks
  const replaceAttributesInBlocks = (blocks, attributes) => {
    return blocks.map(block => {
      const newBlock = { ...block };
      if (block.type === 'richtext' && block.value) {
        newBlock.value = replaceAttributes(block.value, attributes);
      } else if (block.type === 'button' && block.value) {
        newBlock.value = {
          ...block.value,
          text: replaceAttributes(block.value.text, attributes),
          link: replaceAttributes(block.value.link, attributes)
        };
      } else if (block.type === 'image' && block.value) {
        newBlock.value = {
          ...block.value,
          src: replaceAttributes(block.value.src, attributes)
        };
      }
      return newBlock;
    });
  };

  // Build HTML from blocks for test email
  const buildBodyHtml = (blocks) => {
    return blocks.map((block, idx) => {
      const align = block.align || 'left';
      let alignStyle = '';
      if (align === 'center') alignStyle = 'text-align:center;';
      if (align === 'right') alignStyle = 'text-align:right;';
      switch (block.type) {
        case 'richtext':
          return `<div style="${alignStyle}">${block.value || ''}</div>`;
        case 'button':
          if (!block.value.text || !block.value.link) return '';
          let btnWrap = align === 'center' ? 'margin:16px auto;text-align:center;display:block;' : align === 'right' ? 'margin:16px 0 16px auto;text-align:right;display:block;' : 'margin:16px 0;text-align:left;display:block;';
          return `<div style="${btnWrap}"><a href="${block.value.link}" style="background:#2563eb;color:#fff;padding:12px 24px;border-radius:6px;text-decoration:none;display:inline-block;font-weight:600;">${block.value.text}</a></div>`;
        case 'image':
          if (!block.value.src) return '';
          const w = block.value.width ? `width:${block.value.width}px;` : '';
          const h = block.value.height ? `height:${block.value.height}px;` : '';
          let imgWrap = align === 'center' ? 'text-align:center;' : align === 'right' ? 'text-align:right;' : 'text-align:left;';
          return `<div style="margin:16px 0;${imgWrap}"><img src="${block.value.src}" style="max-width:100%;${w}${h}" alt="Image" /></div>`;
        default:
          return '';
      }
    }).join('');
  };

  // Test email handler
  const handleTestEmail = async (e) => {
    e.preventDefault();
    setTestResult(null);
    setTestLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Replace attributes in subject and preview text
      const processedSubject = replaceAttributes(testTemplate.subject, testAttributes);
      const processedPreviewText = replaceAttributes(testTemplate.preview_text, testAttributes);
      
      // Replace attributes in body blocks
      const processedBlocks = replaceAttributesInBlocks(testBodyBlocks, testAttributes);
      const html = buildBodyHtml(processedBlocks);
      
      const response = await axios.post('/api/emails/test', {
        to: testEmailFields.to.filter(Boolean),
        cc: testEmailFields.cc.filter(Boolean),
        bcc: testEmailFields.bcc.filter(Boolean),
        subject: processedSubject,
        previewText: processedPreviewText,
        html
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestResult(response.data);
    } catch (error) {
      setTestResult({
        success: false,
        error: error.response?.data?.error || 'Failed to send test email'
      });
    } finally {
      setTestLoading(false);
    }
  };

  // Save template handler
  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    setSaveResult(null);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Automatically extract attributes from all content
      const subjectAttrs = extractAttributes(templateFields.subject);
      const previewAttrs = extractAttributes(templateFields.previewText);
      const bodyAttrs = extractAttributesFromBlocks(bodyBlocks);
      
      // Combine all attributes and remove duplicates
      const allAttributes = [...new Set([...subjectAttrs, ...previewAttrs, ...bodyAttrs])];
      
      const requestData = {
        title: templateFields.title,
        description: templateFields.description,
        subject: templateFields.subject,
        preview_text: templateFields.previewText,
        body_blocks: bodyBlocks,
        to: templateFields.to.filter(Boolean),
        cc: templateFields.cc.filter(Boolean),
        bcc: templateFields.bcc.filter(Boolean),
        to_programmed: templateFields.toProgrammed,
        cc_programmed: templateFields.ccProgrammed,
        bcc_programmed: templateFields.bccProgrammed,
        allowed_attributes: allAttributes,
        is_active: true
      };
      
      console.log('Sending request data:', requestData);
      
      let response;
      if (isEditing && editingTemplate) {
        // Update existing template
        response = await axios.put(`/api/emailtemplates/${editingTemplate.id}`, requestData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new template
        response = await axios.post('/api/emailtemplates', requestData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setSaveResult({
        success: true,
        message: isEditing ? 'Template updated successfully!' : 'Template saved successfully!',
        templateId: response.data.id
      });
      
      // Reset form after successful save
      setTemplateFields({
        title: '',
        description: '',
        subject: '',
        previewText: '',
        to: [],
        cc: [],
        bcc: [],
        toProgrammed: true,
        ccProgrammed: true,
        bccProgrammed: true
      });
      setBodyBlocks([{ ...defaultBlock }]);
      setIsEditing(false);
      setEditingTemplate(null);
    } catch (error) {
      setSaveResult({
        success: false,
        error: error.response?.data?.error || 'Failed to save template'
      });
    } finally {
      setLoading(false);
    }
  };

  // Clear form and start fresh
  const handleNewTemplate = () => {
    setTemplateFields({
      title: '',
      description: '',
      subject: '',
              previewText: '',
      to: [],
      cc: [],
      bcc: [],
      toProgrammed: true,
      ccProgrammed: true,
      bccProgrammed: true
    });
    setBodyBlocks([{ ...defaultBlock }]);
    setIsEditing(false);
    setEditingTemplate(null);
    setSaveResult(null);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%)', padding: '48px 0', fontFamily: 'inherit' }}>
      {/* Modern Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: 48,
        padding: '40px 24px',
        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
        borderRadius: 24,
        color: 'white',
        boxShadow: '0 10px 30px rgba(5,150,105,0.18)',
        position: 'relative',
        overflow: 'hidden',
        maxWidth: 900,
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"0.05\"%3E%3Ccircle cx=\"30\" cy=\"30\" r=\"2\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }} />
        
        {/* Templates Button */}
        <button
          onClick={() => setShowTemplatesModal(true)}
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            background: 'rgba(255,255,255,0.15)',
            border: '2px solid rgba(255,255,255,0.3)',
            borderRadius: 12,
            padding: '12px 20px',
            color: 'white',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            backdropFilter: 'blur(10px)',
            zIndex: 10
          }}
          onMouseEnter={e => {
            e.target.style.background = 'rgba(255,255,255,0.25)';
            e.target.style.borderColor = 'rgba(255,255,255,0.5)';
          }}
          onMouseLeave={e => {
            e.target.style.background = 'rgba(255,255,255,0.15)';
            e.target.style.borderColor = 'rgba(255,255,255,0.3)';
          }}
        >
          📋 Templates
        </button>

        <h1 style={{
          marginBottom: 8,
          fontWeight: 800,
          letterSpacing: 1.5,
          fontSize: 36,
          position: 'relative',
          zIndex: 1
        }}>
          Email Template Generator
        </h1>
        <div style={{
          fontStyle: 'italic',
          fontSize: 20,
          opacity: 0.92,
          fontWeight: 500,
          position: 'relative',
          zIndex: 1
        }}>
          Create reusable email templates with dynamic attributes
        </div>
      </div>

      {/* Template Generator Card */}
      <div style={{ maxWidth: 800, margin: '0 auto', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', padding: 40, border: '1px solid rgba(16, 185, 129, 0.08)', marginBottom: 40 }}>
        {/* Section Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 6, height: 32, background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', borderRadius: 3 }} />
            <div style={{ fontSize: 26, fontWeight: 800, color: '#059669', letterSpacing: 1 }} title="Create a reusable email template that can be used for bulk sending with dynamic attributes.">
              {isEditing ? `Editing: ${editingTemplate?.title}` : 'Create New Template'}
            </div>
          </div>
          {isEditing && (
            <button
              onClick={handleNewTemplate}
              style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                color: 'white',
                border: 'none',
                borderRadius: 10,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(245,158,11,0.2)'
              }}
            >
              ✨ New Template
            </button>
          )}
        </div>

        <form onSubmit={handleSaveTemplate}>
          {/* Template Metadata */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontWeight: 700, fontSize: 18, color: '#374151', marginBottom: 16 }}>Template Information</div>
            
            {/* Title */}
            <div style={{ position: 'relative', marginBottom: 24 }}>
              <input
                type="text"
                value={templateFields.title}
                onChange={e => setTemplateFields(prev => ({ ...prev, title: e.target.value }))}
                style={{ width: '100%', padding: '18px 16px 8px 16px', border: '2px solid #e5e7eb', borderRadius: 12, fontSize: 17, background: '#fff', fontWeight: 500, outline: 'none', transition: 'border-color 0.2s' }}
                placeholder="Template title"
                required
              />
              <label style={{ position: 'absolute', left: 18, top: 8, color: '#059669', fontSize: 14, fontWeight: 600, background: '#fff', padding: '0 4px' }}>Template Title</label>
            </div>

            {/* Description */}
            <div style={{ position: 'relative', marginBottom: 24 }}>
              <textarea
                value={templateFields.description}
                onChange={e => setTemplateFields(prev => ({ ...prev, description: e.target.value }))}
                style={{ width: '100%', padding: '18px 16px 8px 16px', border: '2px solid #e5e7eb', borderRadius: 12, fontSize: 17, background: '#fff', fontWeight: 500, outline: 'none', transition: 'border-color 0.2s', minHeight: 80, resize: 'vertical' }}
                placeholder="Template description (optional)"
                rows={3}
              />
              <label style={{ position: 'absolute', left: 18, top: 8, color: '#059669', fontSize: 14, fontWeight: 600, background: '#fff', padding: '0 4px' }}>Description</label>
            </div>

            {/* Attribute Detection Note */}
            <div style={{ marginBottom: 24, padding: 16, background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderRadius: 12, border: '1px solid #f59e0b' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 20 }}>🏷️</span>
                <span style={{ fontWeight: 600, color: '#92400e', fontSize: 15 }}>Dynamic Attributes</span>
              </div>
              <div style={{ fontSize: 14, color: '#92400e', lineHeight: 1.4 }}>
                Use the <span style={{ fontWeight: 700 }}>🏷️</span> button to insert dynamic attributes like <code style={{ background: '#fbbf24', padding: '2px 6px', borderRadius: 4, fontSize: 13 }}>{'${attribute_name}'}</code>. 
                Attributes are automatically detected and saved when you create the template.
              </div>
            </div>
          </div>

          {/* Email Content */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontWeight: 700, fontSize: 18, color: '#374151', marginBottom: 16 }}>Email Content</div>
            <EmailComposer
              emailFields={templateFields}
              onEmailFieldsChange={setTemplateFields}
              bodyBlocks={bodyBlocks}
              onBodyBlocksChange={setBodyBlocks}
              showAttributeInserter={true}
              showProgrammedToggles={true}
            >
              {/* Save Template Button */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  background: loading ? 'linear-gradient(135deg, #6ee7b7 0%, #34d399 100%)' : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  color: 'white',
                  padding: '18px 0',
                  border: 'none',
                  borderRadius: 16,
                  fontWeight: 800,
                  fontSize: 20,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  letterSpacing: 1,
                  boxShadow: loading ? 'none' : '0 8px 25px rgba(5,150,105,0.18)',
                  transition: 'all 0.3s',
                  marginTop: 8
                }}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', animation: 'pulse 1.2s infinite' }}>
                    {isEditing ? '💾 Updating...' : '💾 Saving...'}
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                    {isEditing ? '💾 Update Template' : '💾 Save Template'}
                  </span>
                )}
              </button>
            </EmailComposer>
          </div>
        </form>

        {/* Save Result */}
        {saveResult && (
          <div style={{ marginTop: 32, padding: 24, borderRadius: 16, boxShadow: '0 2px 8px rgba(60,72,100,0.07)', background: saveResult.success ? 'linear-gradient(135deg, #d1fae5 0%, #bbf7d0 100%)' : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', color: saveResult.success ? '#065f46' : '#b91c1c', fontWeight: 700, fontSize: 18, display: 'flex', alignItems: 'center', gap: 12, animation: saveResult.success ? 'pulse 1.2s 2' : 'shake 0.5s 2' }}>
            <span style={{ fontSize: 28 }}>{saveResult.success ? '✅' : '❌'}</span>
            <span>{saveResult.success ? 'Success!' : 'Error'}</span>
            <span style={{ fontWeight: 500, fontSize: 15, color: '#444', marginLeft: 12 }}>{saveResult.message || saveResult.error}</span>
            {saveResult.templateId && (
              <span style={{ fontSize: 13, color: '#666', marginLeft: 18 }}>Template ID: {saveResult.templateId}</span>
            )}
          </div>
        )}
      </div>

      {/* Templates Modal */}
      {showTemplatesModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 32,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            maxWidth: 800,
            width: '100%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 28, fontWeight: 800, color: '#059669', margin: 0 }}>📋 Email Templates</h2>
              <button
                onClick={() => setShowTemplatesModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#666',
                  padding: 8
                }}
              >
                ✕
              </button>
            </div>

            {/* Templates List */}
            <div style={{ flex: 1, overflow: 'auto', marginBottom: 24 }}>
              {templatesLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ fontSize: 24, marginBottom: 12 }}>⏳</div>
                  <div style={{ fontSize: 16, color: '#666' }}>Loading templates...</div>
                </div>
              ) : templates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
                  <div style={{ fontSize: 18, color: '#666', marginBottom: 8 }}>No templates found</div>
                  <div style={{ fontSize: 14, color: '#999' }}>Create your first email template to get started</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {templates.map(template => (
                    <div key={template.id} style={{
                      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                      borderRadius: 16,
                      padding: 20,
                      border: '1px solid #e3e8f0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#374151', margin: '0 0 8px 0' }}>
                            {template.title}
                          </h3>
                          {template.description && (
                            <p style={{ fontSize: 14, color: '#666', margin: '0 0 8px 0', lineHeight: 1.4 }}>
                              {template.description}
                            </p>
                          )}
                          <div style={{ fontSize: 13, color: '#888' }}>
                            Subject: <span style={{ fontWeight: 500, color: '#374151' }}>{template.subject}</span>
                          </div>
                          {template.allowed_attributes && template.allowed_attributes.length > 0 && (
                            <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>
                              Attributes: <span style={{ fontWeight: 500, color: '#059669' }}>
                                {template.allowed_attributes.join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
                          <button
                            onClick={() => loadTemplateForTest(template.id)}
                            style={{
                              background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: 8,
                              padding: '8px 16px',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                              boxShadow: '0 2px 8px rgba(139,92,246,0.2)'
                            }}
                            title="Test this template"
                          >
                            🧪 Test
                          </button>
                          <button
                            onClick={() => loadTemplate(template.id, true)}
                            style={{
                              background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: 8,
                              padding: '8px 16px',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                              boxShadow: '0 2px 8px rgba(245,158,11,0.2)'
                            }}
                            title="Duplicate this template"
                          >
                            📋 Duplicate
                          </button>
                          <button
                            onClick={() => loadTemplate(template.id, false)}
                            style={{
                              background: 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: 8,
                              padding: '8px 16px',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                              boxShadow: '0 2px 8px rgba(37,99,235,0.2)'
                            }}
                            title="Edit this template"
                          >
                            ✏️ Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                onClick={() => setShowTemplatesModal(false)}
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: 10,
                  padding: '12px 24px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Template Modal */}
      {showTestModal && testTemplate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 32,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            maxWidth: 900,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 28, fontWeight: 800, color: '#8b5cf6', margin: 0 }}>🧪 Test Template: {testTemplate.title}</h2>
              <button
                onClick={() => setShowTestModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#666',
                  padding: 8
                }}
              >
                ✕
              </button>
            </div>

            {/* Test Content */}
            <div style={{ flex: 1, overflow: 'auto', marginBottom: 24 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, height: '100%' }}>
                {/* Left Column - Attributes and Email Fields */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {/* Dynamic Attributes */}
                  {Object.keys(testAttributes).length > 0 && (
                    <div style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius: 16, padding: 20, border: '1px solid #e3e8f0' }}>
                      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#8b5cf6', margin: '0 0 16px 0' }}>🏷️ Dynamic Attributes</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {Object.keys(testAttributes).map(attr => (
                          <div key={attr} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <label style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{attr}</label>
                            <input
                              type="text"
                              value={testAttributes[attr]}
                              onChange={e => setTestAttributes(prev => ({ ...prev, [attr]: e.target.value }))}
                              style={{ padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', transition: 'border-color 0.2s' }}
                              placeholder={`Enter value for ${attr}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Email Fields */}
                  <div style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius: 16, padding: 20, border: '1px solid #e3e8f0' }}>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: '#8b5cf6', margin: '0 0 16px 0' }}>📧 Email Fields</h3>
                    <div style={{ marginBottom: 16, padding: 12, background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderRadius: 8, border: '1px solid #f59e0b' }}>
                      <div style={{ fontSize: 13, color: '#92400e', fontWeight: 500 }}>
                        💡 <strong>Test Mode:</strong> You can enter test email addresses here, even if the template has programmed recipients enabled.
                      </div>
                    </div>
                    <EmailComposer
                      emailFields={testEmailFields}
                      onEmailFieldsChange={setTestEmailFields}
                      bodyBlocks={testBodyBlocks}
                      onBodyBlocksChange={setTestBodyBlocks}
                      showAttributeInserter={false}
                      showProgrammedToggles={false}
                      readOnly={false}
                      allowTestInput={true}
                    />
                  </div>
                </div>

                {/* Right Column - Preview */}
                <div style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius: 16, padding: 20, border: '1px solid #e3e8f0' }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#8b5cf6', margin: '0 0 16px 0' }}>👁️ Email Preview</h3>
                  <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb', minHeight: 400 }}>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Subject:</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>
                        {replaceAttributes(testTemplate.subject, testAttributes)}
                      </div>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Preview Text:</div>
                      <div style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}>
                        {replaceAttributes(testTemplate.preview_text, testAttributes)}
                      </div>
                    </div>
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Body:</div>
                      <div 
                        style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{ 
                          __html: buildBodyHtml(replaceAttributesInBlocks(testBodyBlocks, testAttributes)) 
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 14, color: '#666' }}>
                Fill in the attributes above to see a live preview of your email
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={() => setShowTestModal(false)}
                  style={{
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: 10,
                    padding: '12px 24px',
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
                <button
                  onClick={handleTestEmail}
                  disabled={testLoading}
                  style={{
                    background: testLoading ? 'linear-gradient(135deg, #a78bfa 0%, #c4b5fd 100%)' : 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 10,
                    padding: '12px 24px',
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: testLoading ? 'not-allowed' : 'pointer',
                    boxShadow: testLoading ? 'none' : '0 4px 12px rgba(139,92,246,0.2)'
                  }}
                >
                  {testLoading ? '📤 Sending...' : '📤 Send Test Email'}
                </button>
              </div>
            </div>

            {/* Test Result */}
            {testResult && (
              <div style={{ marginTop: 16, padding: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(60,72,100,0.07)', background: testResult.success ? 'linear-gradient(135deg, #d1fae5 0%, #bbf7d0 100%)' : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', color: testResult.success ? '#065f46' : '#b91c1c', fontWeight: 600, fontSize: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 24 }}>{testResult.success ? '✅' : '❌'}</span>
                <span>{testResult.success ? 'Test email sent successfully!' : 'Failed to send test email'}</span>
                <span style={{ fontWeight: 500, fontSize: 14, color: '#444', marginLeft: 12 }}>{testResult.message || testResult.error}</span>
                {testResult.messageId && (
                  <span style={{ fontSize: 12, color: '#666', marginLeft: 18 }}>Message ID: {testResult.messageId}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Keyframes for animations */}
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.6; }
          100% { opacity: 1; }
        }
        @keyframes shake {
          0% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-8px); }
          80% { transform: translateX(8px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default EmailTemplateGenerator; 